/**
 * The federal estate: what the polity has built on the district's map.
 *
 * `DESIGN-GOVERNORS.md` §6 specifies six facilities — data centre, factory, gas
 * plant, solar farm, waterworks, logistics hub — as *"federal buildings owned by
 * the polity, placed as rectangles onto district maps"*:
 *
 * > The cells beneath are sealed: bushes destroyed, portals under the slab
 * > permanently closed, the land unavailable to the mayor. Facilities are a mixed
 * > blessing by design — every one of them is somebody's jobs and somebody's
 * > noise.
 *
 * One of the six is built, and it is the data centre, because it is the only one
 * whose *needs* are the two flows this district has. A factory yields goods and a
 * solar farm needs sunlight; both would mean inventing numbers for mechanics that
 * do not exist, and the numbers in §6's table are qualitative (*"Power (heavy)"*)
 * precisely where they would have to be invented. See `docs/facilities.md`.
 *
 * ## What a facility is, here
 *
 * Seven things, and it was five for three ADRs:
 *
 *   0. **A building site**, for `buildSeasons` seasons before it is any of the
 *      rest. §7's `CONSTRUCTION` phase: *"(k seasons) — The footprint hoards;
 *      construction traffic is the heaviest, most ant-lethal traffic in the game;
 *      goods convoys arrive each season or progress stalls."* The hoard is
 *      literal (`FacilityBuild.hoard`), the convoys are trucks on the district's
 *      roads, and a season whose goods did not arrive is a season of no progress
 *      rather than a season of loss. Until it opens the facility draws nothing,
 *      posts no jobs, produces nothing and is metered for nothing — it is
 *      twenty-four sealed cells and a delivery address.
 *   1. **A footprint.** A rectangle of `SEALED` cells: no bush bears fruit on it,
 *      no house is sited on it, no car drives over it, and the mayor's brush does
 *      nothing to it. Permanent (design §19: no un-paving), and sealed from the
 *      pour rather than from the opening — the land is taken on day one.
 *   2. **A load.** Power and water, drawn every budget cycle beside the town's
 *      own, and *last* in both rationing orders — which is to say first in
 *      practice, since a district switches off everything of its own before it
 *      touches a federal load it has no authority over.
 *   3. **A staff requirement.** §6's table says eight families for a data centre
 *      and `DATA_CENTER_STAFF` says how many this world wants — which may be
 *      **zero**, and a world in which it is zero is a world this class runs
 *      unchanged. Who fills the posts is `LabourManager`'s, because this class
 *      cannot see a family.
 *   4. **A rent.** The statutory compensation a seizure owes the host district,
 *      paid pro rata every budget cycle into the treasury whatever the machine is
 *      doing. §6's *"Land lease"*, at §7's *"only statutory compensation"* rate.
 *   5. **An uptime record.** `min(power, water, staffing)` per metered cycle,
 *      accumulated. That is the measurement §7's contracts settle from (*"min
 *      uptime"*) and Law 5 wants the measurement to predate the contract.
 *
 *   6. **A yield, and what it cost to get.** §4's rule is
 *      `min(power, water, staffing) × capacity`, and this is the multiplication.
 *      Compute-units on one side, operating dollars on the other, both scaled by
 *      the same uptime — so a curtailed facility produces less *and spends less*,
 *      which is what carries a governor's priority list down to a shopkeeper.
 *
 * The sixth was absent for three ADRs, and what it was waiting for now exists.
 * ADR-015 D95 deferred production because *"there is no compute flow for the
 * product to be, and no world market to sell it into"*; `MarketManager` is that
 * market. **Nothing here knows the price.** This class counts units made and
 * dollars spent making them; what a unit fetches is the world's business, and
 * this file has never been able to see the world.
 *
 * ## Every number here is counted
 *
 * Law 1: demand is a footprint times a rate, rent is a rate times a facility,
 * uptime is a sum over cycles that were actually served, and yield is that sum
 * times a capacity. No facility is scored, rated, or given a condition. A
 * district with no facility on it is a district in which this class does nothing
 * and hashes as though it were not here.
 *
 * ## What this class cannot see
 *
 * The ground, a hash type, and the protocol's list of names. Not the houses, not
 * the traffic, not the bushes, and not — the point of the arrangement — anything
 * under the soil. It seals cells and reports which cells it sealed; `SurfaceSim`
 * is what pours concrete over them in the sense the rest of the game means by
 * that, which is why the file that decides where a facility goes has never heard
 * of a colony. That is the standard `SeasonManager` and the two flows are held to,
 * and `tests/purity.test.ts` pins this file's import list to keep it.
 */

import { FacilityTypeName, FACILITY_TYPE_NAMES } from '@antification/protocol';
import { Canonical } from '../hashState';
import { CYCLES_PER_SEASON } from '../SimClock';
import { CellState, GridCoord, TerrainGrid } from './TerrainGrid';

/**
 * A facility's fixed geometry, from §6's table.
 *
 * Not configuration: a data centre is 6×4 because the design says so, and a
 * settings slider that changed it would change what the word means. The *rates*
 * are configuration and live in `FacilityConfig`.
 */
export interface FacilitySpec {
  /** What the status panel calls it. Never drawn on the building. */
  label: string;
  cols: number;
  rows: number;
}

export const FACILITY_SPECS: Readonly<Record<FacilityTypeName, FacilitySpec>> = Object.freeze({
  [FacilityTypeName.DATA_CENTER]: Object.freeze({
    label: 'Data Centre',
    cols: 6,
    rows: 4,
  }),
});

/**
 * What a facility draws. World parameters, not state.
 *
 * §6 gives the data centre *"Power (heavy), water (heavy)"* and no numbers, so
 * these are chosen, and the choice is the one thing in this file that could hide
 * an authored delta. What they are chosen *against* is written down in
 * `docs/facilities.md` and in ADR-015 D87: a single data centre must be a load a
 * district notices, and it must not be one that a district can never carry.
 */
export interface FacilityConfig {
  /** The data centre's draw, in kW. Against a 500 kW interconnect. */
  DATA_CENTER_KW: number;
  /** Its cooling water, in m³ per budget cycle. Against a 200 m³ mains. */
  DATA_CENTER_M3: number;
  /**
   * Families it takes to run. §6's *"Staff (families)"* column.
   *
   * Configuration, and **zero is a supported value** — which is the whole reason
   * this is not a property of `FacilitySpec` any more. ADR-016 put it there and
   * called it geometry, on the argument that a data centre needs eight families
   * because the design says so. That argument was wrong about what kind of world
   * this is.
   *
   * Parkington already pays a universal income; a district where nobody has to
   * work is not a variant of this game, it is the direction the whole thing
   * points. A facility with `DATA_CENTER_STAFF: 0` posts no jobs, hires nobody,
   * generates no commute, pays no wages, and runs at full uptime — and the
   * mayor's one instrument of refusal is worth exactly nothing against it. That
   * has to be a number somebody can set, and it has to work.
   *
   * What a job *pays* is `LabourConfig`'s.
   */
  DATA_CENTER_STAFF: number;
  /**
   * What the polity pays the host district for the ground, per season.
   *
   * §6's revenue split opens with *"Land lease | District government pool |
   * Negotiated in siting contract"*, and there is no siting contract, because
   * there is nobody to negotiate one with. What there is instead is §7's
   * description of what a seizure owes: the governor *"places the rectangle
   * anywhere, immediately, paying only **statutory compensation**"*.
   *
   * So this is the statutory rate, not a lease, and it is deliberately set below
   * every figure §7's worked exchange puts on a negotiated one — 5,000 offered,
   * 6,500 asked, 6,000 settled. Law 6 asks that force be cheaper now and more
   * expensive later; this is the first arithmetic in the build that says by how
   * much. A mayor who is *asked* should be able to do better than one who is not.
   *
   * Paid pro rata every budget cycle, whatever the facility's uptime and whatever
   * its staff are doing. It is rent on the land, and the land is sealed either
   * way.
   */
  DATA_CENTER_LEASE: number;
  /**
   * Compute-units a data centre yields per budget cycle at full uptime.
   *
   * §4's *capacity*. The unit is a unit: §5 lists compute as a flow and gives it
   * no denomination, so this counts whatever a data centre sells and
   * `MarketManager.COMPUTE_PRICE` says what one is worth. Inventing a FLOP would
   * have been inventing a second number to hide the first one behind.
   *
   * Chosen so that one data centre is the largest economic object on a district's
   * map by a wide margin, because that is what a data centre is. See
   * `docs/world-market.md` for the arithmetic and ADR-025 D166 for the argument.
   */
  DATA_CENTER_COMPUTE: number;
  /**
   * Dollars a data centre spends running, per budget cycle at full uptime.
   *
   * Power and water are billed separately and to the district (`FiscalManager`),
   * so this is everything else — parts, contractors, consumables, the things a
   * machine hall buys to keep running. §6's revenue split spends a fraction of it
   * locally.
   *
   * **Scaled by uptime**, which is the load-bearing property: a facility the grid
   * curtailed spends proportionally less, so it orders proportionally less from
   * the shops down the road. That is the arrow from §5's priority list to §10's
   * quality of life, and it runs through a multiplication rather than through
   * anything that knows a shopkeeper exists.
   */
  DATA_CENTER_OPEX: number;
  /**
   * Seasons of construction before a data centre opens. §7's *"(k seasons)"*.
   *
   * **Two**, and the number is §7's own — but the *other* one it gives. The
   * worked exchange in §7 awards a volunteered site and notes *"Construction: 2
   * seasons instead of 4, because the pad was ready"*, so the design prices an
   * unprepared site at four seasons and a readied one at two. A forced siting is
   * the unprepared case and should therefore be the longer one.
   *
   * This build ships the shorter number anyway, and the reason is that it cannot
   * charge the difference honestly: readiness is `prepare_site(cells, works)`
   * (§10), which is a mayor's verb inside the Siting Protocol, and there is no
   * Siting Protocol here. Four seasons would be charging a force premium that
   * the design says is earned by the *absence* of works nobody in this build can
   * build. When the protocol arrives (roadmap item 8) force gets four and
   * readiness buys it down to two, which is Q71.
   *
   * **Zero is supported and means what it says**: a facility that opens on the
   * tick its slab is poured, which is every facility this game had before
   * ADR-029. It is the setting a world runs on if construction is not the part
   * of the design it is interested in.
   */
  DATA_CENTER_BUILD_SEASONS: number;
  /**
   * Goods one season of construction needs delivered. §7's *"goods convoys"*.
   *
   * **One goods-unit is one truck**, which is not a simplification but §5's own
   * sentence: *"Goods […] Trucks. Every unit of goods that moves is a truck on
   * someone's map."* So this number is also the season's convoy count, and at
   * 120 over a 90-cycle season it is a truck and a third every budget cycle —
   * more traffic than the shipped brief routes through a corridor, aimed at one
   * address, which is §7's *"the heaviest, most ant-lethal traffic in the game"*
   * expressed as volume rather than as a multiplier on a wear rate.
   *
   * What the goods *cost* is the world's business and lives in `MarketConfig`;
   * what they weigh on a road is a truck's business and lives in `FreightConfig`.
   * Nothing here knows either figure.
   *
   * Zero is supported: a world in which construction takes time and no delivery.
   */
  DATA_CENTER_GOODS: number;
}

export const DEFAULT_FACILITY_CONFIG: FacilityConfig = {
  DATA_CENTER_KW: 180,
  DATA_CENTER_M3: 45,
  DATA_CENTER_STAFF: 8,
  DATA_CENTER_LEASE: 3000,
  DATA_CENTER_COMPUTE: 40,
  DATA_CENTER_OPEX: 1600,
  DATA_CENTER_BUILD_SEASONS: 2,
  DATA_CENTER_GOODS: 120,
};

/** One facility, as it stands on the map. */
export interface Facility {
  id: number;
  type: FacilityTypeName;
  /** North-west corner of the footprint. */
  x: number;
  y: number;
  cols: number;
  rows: number;
  /** The tick the slab was poured on. What a Chronicle would cite. */
  placedTick: number;
  /**
   * The tick it opened on, or `null` while it is still a building site.
   *
   * The whole of the phase, as one nullable field rather than an enum: §7 has
   * `CONSTRUCTION` and then `OPERATION` and nothing between them, and a facility
   * that has opened has a date. `null` is the state every consumer in this class
   * checks — a site draws nothing, staffs nobody, produces nothing and is metered
   * for nothing, and each of those is one `isOperating` away.
   */
  openedTick: number | null;
}

/**
 * What a facility under construction has taken delivery of. §7's `CONSTRUCTION`.
 *
 * Kept for the life of the facility rather than discarded at the opening: the
 * lifetime counters are what a Chronicle publishes and what a §7 contract would
 * settle a construction schedule from, and a record that deleted itself the moment
 * the thing it recorded succeeded would be the one kind of ledger this build has
 * never written.
 */
export interface FacilityBuild {
  /** Seasons of progress banked. It opens when this reaches `buildSeasons`. */
  seasonsBuilt: number;
  /**
   * Goods delivered and not yet spent on a season of progress.
   *
   * §7's *"The footprint hoards"*, literally. A season whose deliveries fell
   * short is a season with no progress and a hoard that keeps what arrived, so
   * bad roads make a build **slower** rather than impossible — which is the
   * difference between a district that can be forced upon and a district that
   * holds a veto (Law 6: force must stay *available*, and expensive).
   */
  hoard: number;
  /**
   * Goods owed but not yet ordered, in **goods-cycles**. See `convoysDue`.
   *
   * A season's goods over a season's cycles is not a whole number of lorries, so
   * something has to carry the remainder. It is carried in the numerator rather
   * than as a fraction — `pending += perSeason`, release `⌊pending / 90⌋` — because
   * both figures are integers and integer arithmetic has no remainder to lose.
   * `FreightManager.pending` carries a float and drifts by a leg over a season;
   * this is the same idiom with the division done last.
   */
  pending: number;
  /** Convoys on the road right now: dispatched, not yet arrived or lost. */
  inTransit: number;
  /** Goods that reached the gate, lifetime. Also the count of convoys that did. */
  goodsDelivered: number;
  /** Convoys that became a truck on the map, lifetime. */
  convoysDispatched: number;
  /** Trucks that gave up on the way, lifetime. The polity paid for these. */
  convoysLost: number;
  /**
   * Convoys with nowhere to enter, lifetime.
   *
   * A district with no interchange and no road on its boundary has no way in for
   * a lorry, and one whose way in is blocked by a parked car has no room at it.
   * Neither is bought and neither becomes a truck; the polity ships again next
   * cycle. `FreightLedger.legsLapsed`'s sibling, for the same reason.
   */
  convoysLapsed: number;
}

/**
 * Where a facility's staff arrive, and where its trucks would.
 *
 * The middle of the slab's south edge — a cell of the footprint itself, not one
 * outside it. Two reasons, and they are both about not inventing anything: a cell
 * of the footprint is always on the map, whereas a cell beyond the south edge of a
 * facility sited against the bottom of the district is not; and a car arrives at a
 * building when it reaches a paved cell within two of it, which is a rule that has
 * been in `CarManager` since long before there were facilities. So the gate is
 * where the front door would be, and a facility with no road within two cells of
 * its front door is a facility nobody can get to.
 *
 * That last sentence is a mechanic and not an oversight. §10 gives a mayor
 * `prepare_site(cells, works)` — *"Pre-build pads, spurs, roads to court
 * facilities"* — and the mirror of courting a facility with a road is hosting one
 * you never built a road to. Nothing here charges anybody for either.
 */
export function facilityGate(facility: Facility): GridCoord {
  return {
    x: facility.x + Math.floor((facility.cols - 1) / 2),
    y: facility.y + facility.rows - 1,
  };
}

/** A facility's own ledger. Monotone sums, so a season's uptime is a subtraction. */
export interface FacilityLedger {
  /** Budget cycles this facility has been metered for. */
  meteredCycles: number;
  /** Cycles it got everything it asked for. */
  servedCycles: number;
  /** Cycles it did not. `meteredCycles - servedCycles`, recorded rather than derived. */
  curtailedCycles: number;
  /**
   * Cycle-fractions actually supplied: `Σ min(power, water, staffing)`.
   *
   * The quantity a `min uptime` contract term is a comparison against, and the
   * one production multiplies.
   */
  uptimeCycles: number;
  /** Compute-units this facility has produced. `Σ uptime × capacity`. */
  computeUnits: number;
  /** Dollars it has spent running. `Σ uptime × opex`. */
  operatingCost: number;
}

/** Why a `FORCE_PLACE` did not land. Information for the caller, not an error. */
export type SitingRefusal =
  | { ok: false; reason: 'off-map' }
  | { ok: false; reason: 'occupied'; x: number; y: number };

export type SitingResult =
  | { ok: true; facility: Facility; sealed: GridCoord[] }
  | SitingRefusal;

/** The estate's complete state. See `FacilityManager.save()`. */
export interface FacilitySnapshot {
  facilities: Facility[];
  nextId: number;
  ledgers: Array<[number, FacilityLedger]>;
  builds: Array<[number, FacilityBuild]>;
  wornMark: number;
  powerFraction: number;
  waterFraction: number;
  staffedFraction: number;
  meteredCycles: number;
  curtailedCycles: number;
  uptimeCycles: number;
  computeUnits: number;
  operatingCost: number;
  leasePaid: number;
}

/**
 * What one metered budget cycle of the estate produced, and what it cost.
 *
 * `meter()`'s return value, so that the caller does not have to re-derive the
 * multiplication from the fractions it just handed in. Both terms are already
 * scaled by `min(power, water, staffing)`.
 */
export interface FacilityOutput {
  computeUnits: number;
  operatingCost: number;
}

/** A facility's uptime at the last metered cycle, for a renderer or a panel. */
export interface FacilityUptime {
  powerFraction: number;
  waterFraction: number;
  /** Posts worked over posts advertised. §4's third term. */
  staffedFraction: number;
  /** `min` of the three. 1 when the facility got everything it asked for. */
  uptime: number;
}

function emptyLedger(): FacilityLedger {
  return {
    meteredCycles: 0,
    servedCycles: 0,
    curtailedCycles: 0,
    uptimeCycles: 0,
    computeUnits: 0,
    operatingCost: 0,
  };
}

function emptyBuild(): FacilityBuild {
  return {
    seasonsBuilt: 0,
    hoard: 0,
    pending: 0,
    inTransit: 0,
    goodsDelivered: 0,
    convoysDispatched: 0,
    convoysLost: 0,
    convoysLapsed: 0,
  };
}

/** One convoy the polity owes a building site this cycle. See `convoysDue`. */
export interface ConvoyOrder {
  /** Which site it is for. Names the delivery in `CarManager`'s outcome tray. */
  facilityId: number;
  /** Where it is going: the site's gate, which is a cell of the footprint. */
  gate: GridCoord;
  /** Goods-units on the lorry. One, and §5 says why (`DATA_CENTER_GOODS`). */
  goods: number;
}

/**
 * The district's federal estate.
 *
 * Owned by `SurfaceSim`, metered on the same budget cycle as the two flows,
 * snapshotted inside it. Holds no `Rng`: where a facility goes is somebody's
 * decision and what it gets is arithmetic, so there is nothing here to roll.
 */
export class FacilityManager {
  private config: FacilityConfig;

  private readonly facilities: Facility[] = [];
  private nextId = 1;

  /**
   * Which facility covers a cell, by `"x,y"`.
   *
   * Derived from the footprints and rebuilt on load, so it is not in the
   * snapshot. It exists because the mayor's brush asks the question on every
   * click and a walk of every footprint per click is a walk too many.
   */
  private readonly cover = new Map<string, number>();

  // --- The last metered cycle -----------------------------------------------

  private powerFraction = 1;
  private waterFraction = 1;
  private staffedFraction = 1;

  // --- The ledgers ----------------------------------------------------------

  private readonly ledgers = new Map<number, FacilityLedger>();

  /**
   * What each facility's construction has come to. One entry per facility, from
   * the pour, kept after it opens (see `FacilityBuild`).
   */
  private readonly builds = new Map<number, FacilityBuild>();

  /**
   * Convoy arrivals already charged to the district's roads.
   *
   * `FreightManager.settledMark`'s twin, and dangerous for the same reason: a
   * district restored with it at zero wears its roads by every lorry that has ever
   * delivered to it, in one cycle, silently. Here rather than in the corridor's
   * ledger because a delivery is not a crossing — it earns no toll, and the two
   * counts must not be able to pay each other's bills.
   */
  private wornMark = 0;

  private meteredCycles = 0;
  private curtailedCycles = 0;
  private uptimeCycles = 0;
  private computeUnits = 0;
  private operatingCost = 0;
  /** Dollars the estate has paid the host district. Monotone. */
  private leasePaid = 0;

  constructor(private readonly grid: TerrainGrid, config: Partial<FacilityConfig> = {}) {
    this.config = { ...DEFAULT_FACILITY_CONFIG, ...config };
  }

  getConfig(): FacilityConfig {
    return { ...this.config };
  }

  updateConfig(partial: Partial<FacilityConfig>): void {
    this.config = { ...this.config, ...partial };
  }

  // --- The estate -----------------------------------------------------------

  /** The facilities on this map, in siting order. */
  getFacilities(): readonly Facility[] {
    return this.facilities;
  }

  /** How many there are. The cheap question, asked every tick. */
  get count(): number {
    return this.facilities.length;
  }

  getFacility(id: number): Facility | undefined {
    return this.facilities.find((f) => f.id === id);
  }

  /**
   * The facilities that have opened, in siting order.
   *
   * Every consumer of the estate but two walks this rather than `facilities`: a
   * building site draws no power, drinks no water, posts no jobs, produces
   * nothing, spends nothing and is metered for nothing. The two that walk the
   * whole list are the ground (a slab is sealed from the pour) and the rent (it is
   * rent on that ground).
   */
  private operating(): Facility[] {
    return this.facilities.filter((f) => f.openedTick !== null);
  }

  /** Whether this facility has opened. `false` while it is a building site. */
  isOperating(id: number): boolean {
    const facility = this.getFacility(id);
    return facility !== undefined && facility.openedTick !== null;
  }

  /** How many have opened. `count` less this is how many are still going up. */
  get operatingCount(): number {
    return this.operating().length;
  }

  /** One facility's construction record. Zeroes for an id nobody sited. */
  getBuild(id: number): FacilityBuild {
    return { ...(this.builds.get(id) ?? emptyBuild()) };
  }

  /** Seasons of construction this type takes before it opens. */
  buildSeasonsFor(type: FacilityTypeName): number {
    switch (type) {
      case FacilityTypeName.DATA_CENTER:
        return Math.max(0, Math.floor(this.config.DATA_CENTER_BUILD_SEASONS));
    }
  }

  /** Goods one season of this type's construction needs. */
  goodsPerSeasonFor(type: FacilityTypeName): number {
    switch (type) {
      case FacilityTypeName.DATA_CENTER:
        return Math.max(0, Math.floor(this.config.DATA_CENTER_GOODS));
    }
  }

  /** The facility covering this cell, or `null`. */
  covers(x: number, y: number): number | null {
    return this.cover.get(`${x},${y}`) ?? null;
  }

  /** Cells under a slab. Equal to the grid's own count, by construction. */
  get sealedCells(): number {
    return this.cover.size;
  }

  /** Families one facility of this type needs. Zero in a fully autonomous world. */
  staffFor(type: FacilityTypeName): number {
    switch (type) {
      case FacilityTypeName.DATA_CENTER:
        return Math.max(0, Math.floor(this.config.DATA_CENTER_STAFF));
    }
  }

  /**
   * Families the estate needs to run. A sum over what each *open* facility asks
   * for — §7's construction phase is explicit that force buys *"no local-hire
   * agreement"* and nothing at all before the machine hall exists, so a building
   * site posts no jobs and a district hosting one has nothing withheld to withhold.
   */
  get staffRequired(): number {
    let staff = 0;
    for (const facility of this.operating()) staff += this.staffFor(facility.type);
    return staff;
  }

  /**
   * What the estate owes the district this budget cycle, in dollars.
   *
   * §6 says the split is paid *"per season"*, and this pays a ninetieth of it
   * ninety times instead. The district's budget settles continuously and has
   * since long before there were seasons; a lease that arrived as one lump every
   * ninety cycles would be a spike in a ledger that has none, and the season
   * pipeline is a pure reader by ADR-012 D63 — giving it a payment to make would
   * be the first thing settlement ever *did*. The total over a season is the same
   * number either way.
   */
  getLeasePerCycle(): number {
    let lease = 0;
    for (const facility of this.facilities) lease += this.leaseOf(facility);
    return lease / CYCLES_PER_SEASON;
  }

  /** What the estate has paid the district over its whole life. */
  getLeasePaid(): number {
    return this.leasePaid;
  }

  /** Book a lease payment that has actually reached a treasury. */
  recordLease(amount: number): void {
    this.leasePaid += amount;
  }

  private leaseOf(facility: Facility): number {
    switch (facility.type) {
      case FacilityTypeName.DATA_CENTER:
        return Math.max(0, this.config.DATA_CENTER_LEASE);
    }
  }

  /** Where this facility's staff arrive, or `null` for an id nobody sited. */
  gateOf(id: number): GridCoord | null {
    const facility = this.getFacility(id);
    return facility ? facilityGate(facility) : null;
  }

  /**
   * Put a facility on the map, whatever is under it.
   *
   * The seizure half of §7's protocol and the only half that exists: no notice
   * was published, no offer was made, no mayor was asked, and nothing here
   * charges the governor for that. Only two things are refused, and both are
   * physical rather than political:
   *
   *   - a footprint that does not fit on the map, because there is nowhere to
   *     put it;
   *   - a cell the caller says is taken — another slab, or a standing building.
   *
   * The second is a predicate rather than a rule, because *what* counts as taken
   * is not this class's business. A house under the rectangle should be
   * demolished, not avoided — §7 is explicit that *"houses under the rectangle
   * are demolished [and] their families join the migration queue with
   * 'displaced' in the Chronicle"* — and neither demolition nor a migration queue
   * exists here. So `SurfaceSim` blocks those cells for now and ADR-015 D86 says
   * that is a deferral rather than a design.
   *
   * Returns the cells it sealed. The caller pours the concrete over them, in the
   * sense the rest of the game means by that: this class does not know that
   * anything lives underneath.
   */
  place(
    type: FacilityTypeName,
    x: number,
    y: number,
    tick: number,
    blocked: (x: number, y: number) => boolean = () => false
  ): SitingResult {
    const spec = FACILITY_SPECS[type];

    for (let dy = 0; dy < spec.rows; dy++) {
      for (let dx = 0; dx < spec.cols; dx++) {
        if (!this.grid.getCell(x + dx, y + dy)) return { ok: false, reason: 'off-map' };
        if (this.covers(x + dx, y + dy) !== null || blocked(x + dx, y + dy)) {
          return { ok: false, reason: 'occupied', x: x + dx, y: y + dy };
        }
      }
    }

    const facility: Facility = {
      id: this.nextId++,
      type,
      x,
      y,
      cols: spec.cols,
      rows: spec.rows,
      placedTick: tick,
      // A siting is a building site, unless this world builds instantly. The
      // land is sealed either way and on this tick: §7's construction phase is a
      // clock on the *machine*, and the meadow is gone the moment the concrete
      // is poured over it.
      openedTick: this.buildSeasonsFor(type) > 0 ? null : tick,
    };
    this.facilities.push(facility);
    this.ledgers.set(facility.id, emptyLedger());
    this.builds.set(facility.id, emptyBuild());

    const sealed: GridCoord[] = [];
    for (let dy = 0; dy < spec.rows; dy++) {
      for (let dx = 0; dx < spec.cols; dx++) {
        const cx = x + dx;
        const cy = y + dy;
        this.cover.set(`${cx},${cy}`, facility.id);
        this.grid.setCellState(cx, cy, CellState.SEALED);
        sealed.push({ x: cx, y: cy });
      }
    }

    return { ok: true, facility, sealed };
  }

  // --- The load -------------------------------------------------------------

  /**
   * What the estate draws from the grid, in kW. A footprint times a rate.
   *
   * Open facilities only. A building site draws **nothing**, and that is a
   * deliberate omission rather than a rounding: §5 lists construction among the
   * things that consume power, and giving a federal site a load would mean
   * inventing the number *and* deciding which class of the shed order it sits in —
   * and the class it would sit in is *"construction sites"*, second from the top,
   * which is the one place a district could switch off something federal. That is
   * a priority-list question and the priority list is the polity's (Q61). See
   * Q70.
   */
  getPowerKw(): number {
    let kw = 0;
    for (const facility of this.operating()) kw += this.powerKwOf(facility);
    return kw;
  }

  /** What it draws from the mains, in m³ per cycle. Open facilities only. */
  getWaterM3(): number {
    let m3 = 0;
    for (const facility of this.operating()) m3 += this.waterM3Of(facility);
    return m3;
  }

  private powerKwOf(facility: Facility): number {
    switch (facility.type) {
      case FacilityTypeName.DATA_CENTER:
        return this.config.DATA_CENTER_KW;
    }
  }

  private waterM3Of(facility: Facility): number {
    switch (facility.type) {
      case FacilityTypeName.DATA_CENTER:
        return this.config.DATA_CENTER_M3;
    }
  }

  // --- The yield ------------------------------------------------------------

  /**
   * Compute-units the estate would produce in a cycle at full uptime.
   *
   * §4's *capacity*, before the `min` is applied. A quote rather than a
   * measurement: what a mayor is being offered, and what `MarketManager` prices
   * a season of when somebody asks whether hosting one is worth it.
   */
  getComputeCapacity(): number {
    let units = 0;
    for (const facility of this.operating()) units += this.computeOf(facility);
    return units;
  }

  /** Dollars the estate would spend in a cycle at full uptime. */
  getOpexCapacity(): number {
    let cost = 0;
    for (const facility of this.operating()) cost += this.opexOf(facility);
    return cost;
  }

  private computeOf(facility: Facility): number {
    switch (facility.type) {
      case FacilityTypeName.DATA_CENTER:
        return Math.max(0, this.config.DATA_CENTER_COMPUTE);
    }
  }

  private opexOf(facility: Facility): number {
    switch (facility.type) {
      case FacilityTypeName.DATA_CENTER:
        return Math.max(0, this.config.DATA_CENTER_OPEX);
    }
  }

  // --- The meter ------------------------------------------------------------

  /**
   * Record what the estate was actually supplied this budget cycle.
   *
   * Three fractions, handed in by `SurfaceSim` after both flows have metered and
   * the labour market has settled. None of the three suppliers is reachable from
   * here and none of them knows this class exists: a facility is a number in a
   * reading on the way in and a fraction on the way out, which is what keeps a
   * flow — or a labour market — from acquiring an opinion about what it is
   * supplying.
   *
   * §4's rule is `min(power, water, staffing) × capacity`, and this is the `min`.
   * The three terms are not alike, and the asymmetry is the point: a district
   * chooses the first by how it develops, receives the second from the weather,
   * and *withholds* the third on purpose. The arithmetic does not care which, and
   * that is what makes the third one leverage.
   *
   * The fraction is shared by every facility rather than rotated between them,
   * unlike a home's brownout. A rota exists so that no one family carries a whole
   * shortage; facilities are not families, and a contract measures a fraction of
   * a season rather than a list of the days it was unlucky.
   */
  meter(
    powerFraction: number,
    waterFraction: number,
    staffedFraction: number
  ): FacilityOutput {
    const operating = this.operating();
    // An estate of building sites is metered for nothing at all, on exactly the
    // terms an empty estate is: there is no supply to record a fraction of, and a
    // curtailed cycle a facility could not have used is a curtailment nobody
    // suffered. §7's `min uptime` clock starts at `OPERATION`.
    if (operating.length === 0) return { computeUnits: 0, operatingCost: 0 };

    this.powerFraction = clampFraction(powerFraction);
    this.waterFraction = clampFraction(waterFraction);
    this.staffedFraction = clampFraction(staffedFraction);
    const uptime = Math.min(this.powerFraction, this.waterFraction, this.staffedFraction);

    this.meteredCycles++;
    this.uptimeCycles += uptime;
    if (uptime < 1) this.curtailedCycles++;

    let computeUnits = 0;
    let operatingCost = 0;
    for (const facility of operating) {
      const units = uptime * this.computeOf(facility);
      const cost = uptime * this.opexOf(facility);
      computeUnits += units;
      operatingCost += cost;

      const ledger = this.ledgers.get(facility.id) ?? emptyLedger();
      ledger.meteredCycles++;
      ledger.uptimeCycles += uptime;
      ledger.computeUnits += units;
      ledger.operatingCost += cost;
      if (uptime < 1) ledger.curtailedCycles++;
      else ledger.servedCycles++;
      this.ledgers.set(facility.id, ledger);
    }

    this.computeUnits += computeUnits;
    this.operatingCost += operatingCost;
    return { computeUnits, operatingCost };
  }

  /** What the estate got at the last metered cycle. */
  getUptime(): FacilityUptime {
    return {
      powerFraction: this.powerFraction,
      waterFraction: this.waterFraction,
      staffedFraction: this.staffedFraction,
      uptime: Math.min(this.powerFraction, this.waterFraction, this.staffedFraction),
    };
  }

  /** True while the estate is running short of either flow. */
  get curtailed(): boolean {
    return this.operatingCount > 0 && this.getUptime().uptime < 1;
  }

  // --- The building site ----------------------------------------------------

  /**
   * The convoys the polity owes its building sites this budget cycle.
   *
   * Called once per settled cycle by `SurfaceSim`, which is the only object that
   * can see both a building site and a way into the district. The rate is the
   * season's goods spread evenly over the season's cycles and accumulated as a
   * fraction — `FreightManager.due()`'s idiom, and stochastic for the same reason
   * it is not: Law 3 forbids dice in politics, and a delivery schedule that rolled
   * would be a construction programme no mayor could plan a road budget against.
   *
   * Two things bound it, and both are about not shipping goods nobody needs. A
   * site never has more goods on the road and in the hoard than the rest of its
   * build requires, so the last cycles of a build are quiet; and a site that has
   * banked every season it needs is finished with lorries whether or not the
   * settlement that opens it has run yet.
   *
   * Whether a convoy becomes a truck is the caller's answer, given back with
   * `convoyDispatched` or `convoyLapsed`.
   */
  convoysDue(): ConvoyOrder[] {
    const orders: ConvoyOrder[] = [];
    for (const facility of this.facilities) {
      if (facility.openedTick !== null) continue;
      const build = this.builds.get(facility.id);
      if (!build) continue;

      const perSeason = this.goodsPerSeasonFor(facility.type);
      if (perSeason <= 0) continue;
      const seasons = this.buildSeasonsFor(facility.type);
      const need =
        (seasons - build.seasonsBuilt) * perSeason - build.hoard - build.inTransit;
      if (need <= 0) continue;

      build.pending += perSeason;
      const due = Math.floor(build.pending / CYCLES_PER_SEASON);
      const whole = Math.min(due, need);
      if (whole <= 0) continue;
      build.pending -= due * CYCLES_PER_SEASON;

      const gate = facilityGate(facility);
      for (let i = 0; i < whole; i++) {
        orders.push({ facilityId: facility.id, gate: { x: gate.x, y: gate.y }, goods: 1 });
      }
    }
    return orders;
  }

  /** A lorry set off for this site. */
  convoyDispatched(facilityId: number): void {
    const build = this.builds.get(facilityId);
    if (!build) return;
    build.convoysDispatched++;
    build.inTransit++;
  }

  /** A convoy that could not enter the district. Nothing was bought for it. */
  convoyLapsed(facilityId: number): void {
    const build = this.builds.get(facilityId);
    if (!build) return;
    build.convoysLapsed++;
  }

  /**
   * A lorry reached the gate, or gave up on the way.
   *
   * An arrival is a goods-unit in the hoard; a loss is a goods-unit the polity
   * paid for and nobody received. The site is not told which lorry it was, for
   * `FreightManager.outcome`'s reason: a delivery exists as a truck between
   * dispatch and outcome, and the truck is the record of it.
   */
  convoyOutcome(facilityId: number, arrived: boolean, goods = 1): void {
    const build = this.builds.get(facilityId);
    if (!build) return;
    build.inTransit = Math.max(0, build.inTransit - 1);
    if (arrived) {
      build.hoard += goods;
      build.goodsDelivered += goods;
    } else {
      build.convoysLost++;
    }
  }

  /**
   * Advance every building site by a season, and open the ones that are done.
   *
   * §3's seventh settlement stage — *"Construction — facility and infrastructure
   * build progress advances"* — and §7's rule for what advances it: *"goods
   * convoys arrive each season or progress stalls."* A season whose hoard covers
   * the season's goods spends them and banks a season; one whose hoard does not
   * spends nothing and banks nothing, and the goods that did arrive stay in the
   * hoard for the next attempt.
   *
   * Returns the facilities that opened, so the caller can say so out loud. This
   * class cannot: it has never been able to reach a news ticker, a colony, or
   * anything else that would notice.
   */
  advanceConstruction(tick: number): Facility[] {
    const opened: Facility[] = [];
    for (const facility of this.facilities) {
      if (facility.openedTick !== null) continue;
      const build = this.builds.get(facility.id);
      if (!build) continue;

      const perSeason = this.goodsPerSeasonFor(facility.type);
      if (build.hoard >= perSeason) {
        build.hoard -= perSeason;
        build.seasonsBuilt++;
      }
      if (build.seasonsBuilt >= this.buildSeasonsFor(facility.type)) {
        facility.openedTick = tick;
        opened.push(facility);
      }
    }
    return opened;
  }

  /**
   * Convoy arrivals not yet charged to the district's roads.
   *
   * Pure, and booked by `recordConvoyWear`: the mark idiom `FreightManager` uses
   * for the toll, kept separate from it because these lorries earn the district
   * **nothing**. §6 pays a toll *"per truck-leg"* to a district *"along freight
   * routes"*, and a delivery to a slab the district never agreed to host is not a
   * crossing it is providing to anybody. It wears the same road at the same rate,
   * and there is nothing on the other side of the ledger.
   */
  convoyWear(): number {
    let delivered = 0;
    for (const facility of this.facilities) {
      delivered += this.builds.get(facility.id)?.goodsDelivered ?? 0;
    }
    return Math.max(0, delivered - this.wornMark);
  }

  /** Book it: the mark advances, and no lorry wears the road twice. */
  recordConvoyWear(legs: number): void {
    if (legs <= 0) return;
    this.wornMark += legs;
  }

  /** One facility's lifetime figures. Zeroes for an id that was never sited. */
  getLedger(id: number): FacilityLedger {
    return { ...(this.ledgers.get(id) ?? emptyLedger()) };
  }

  /** The estate's lifetime figures, operation and construction alike. */
  getEstateLedger() {
    let goodsDelivered = 0;
    let convoysDispatched = 0;
    let convoysLost = 0;
    let convoysLapsed = 0;
    for (const facility of this.facilities) {
      const build = this.builds.get(facility.id);
      if (!build) continue;
      goodsDelivered += build.goodsDelivered;
      convoysDispatched += build.convoysDispatched;
      convoysLost += build.convoysLost;
      convoysLapsed += build.convoysLapsed;
    }
    return {
      meteredCycles: this.meteredCycles,
      curtailedCycles: this.curtailedCycles,
      uptimeCycles: this.uptimeCycles,
      computeUnits: this.computeUnits,
      operatingCost: this.operatingCost,
      leasePaid: this.leasePaid,
      goodsDelivered,
      convoysDispatched,
      convoysLost,
      convoysLapsed,
    };
  }

  // --- State ---------------------------------------------------------------

  save(): FacilitySnapshot {
    return {
      facilities: this.facilities.map((f) => ({ ...f })),
      nextId: this.nextId,
      // Facility order, matching `facilities`, which is siting order. A snapshot
      // preserves order rather than canonicalising it (`core/snapshot.ts`).
      ledgers: this.facilities.map((f) => [f.id, { ...this.getLedger(f.id) }] as [number, FacilityLedger]),
      builds: this.facilities.map((f) => [f.id, { ...this.getBuild(f.id) }] as [number, FacilityBuild]),
      wornMark: this.wornMark,
      powerFraction: this.powerFraction,
      waterFraction: this.waterFraction,
      staffedFraction: this.staffedFraction,
      meteredCycles: this.meteredCycles,
      curtailedCycles: this.curtailedCycles,
      uptimeCycles: this.uptimeCycles,
      computeUnits: this.computeUnits,
      operatingCost: this.operatingCost,
      leasePaid: this.leasePaid,
    };
  }

  /**
   * Restore from `save()`.
   *
   * The cover map is rebuilt from the footprints rather than stored, and the
   * cells are *not* re-sealed: the terrain is restored before this by
   * `SurfaceSim.restore`, and re-sealing here would bump the terrain version and
   * make every loaded district look freshly changed to the caches that watch it.
   */
  load(state: FacilitySnapshot): void {
    this.facilities.length = 0;
    this.cover.clear();
    this.ledgers.clear();
    this.builds.clear();

    for (const facility of state.facilities) {
      this.facilities.push({ ...facility });
      for (let dy = 0; dy < facility.rows; dy++) {
        for (let dx = 0; dx < facility.cols; dx++) {
          this.cover.set(`${facility.x + dx},${facility.y + dy}`, facility.id);
        }
      }
    }
    for (const [id, ledger] of state.ledgers) this.ledgers.set(id, { ...ledger });
    for (const [id, build] of state.builds) this.builds.set(id, { ...build });

    this.wornMark = state.wornMark;
    this.nextId = state.nextId;
    this.powerFraction = state.powerFraction;
    this.waterFraction = state.waterFraction;
    this.staffedFraction = state.staffedFraction;
    this.meteredCycles = state.meteredCycles;
    this.curtailedCycles = state.curtailedCycles;
    this.uptimeCycles = state.uptimeCycles;
    this.computeUnits = state.computeUnits;
    this.operatingCost = state.operatingCost;
    this.leasePaid = state.leasePaid;
  }

  /**
   * Order-stable view of the estate, for hashing — and nothing when it is empty.
   *
   * The same move as the two flows and the Chronicle: an estate with no facility
   * in it and no estate at all are the same world, so the key is omitted rather
   * than written as a bag of zeroes. That is what let a facility arrive without
   * moving a single hash recorded before facilities existed — every corpus entry
   * in this repository is a district nobody has sited anything on.
   *
   * Facilities are sorted by id, which is siting order, so the sort is a promise
   * rather than a change (`docs/determinism.md` rule 4).
   */
  canonicalState(): Canonical | undefined {
    if (this.facilities.length === 0) return undefined;
    const ids = this.facilities.map((f) => f.id).sort((a, b) => a - b);
    return {
      facilities: ids.map((id) => {
        const f = this.getFacility(id)!;
        const ledger = this.getLedger(id);
        const build = this.getBuild(id);
        return {
          id: f.id,
          type: f.type,
          x: f.x,
          y: f.y,
          cols: f.cols,
          rows: f.rows,
          placedTick: f.placedTick,
          // `-1` for a site that has not opened, because a hash key is a number
          // here and `null` would be a second kind of value in a field that has
          // only ever held one. Nothing outside this method reads it.
          openedTick: f.openedTick ?? -1,
          meteredCycles: ledger.meteredCycles,
          servedCycles: ledger.servedCycles,
          curtailedCycles: ledger.curtailedCycles,
          uptimeCycles: ledger.uptimeCycles,
          computeUnits: ledger.computeUnits,
          operatingCost: ledger.operatingCost,
          seasonsBuilt: build.seasonsBuilt,
          hoard: build.hoard,
          pending: build.pending,
          inTransit: build.inTransit,
          goodsDelivered: build.goodsDelivered,
          convoysDispatched: build.convoysDispatched,
          convoysLost: build.convoysLost,
          convoysLapsed: build.convoysLapsed,
        };
      }),
      wornMark: this.wornMark,
      nextId: this.nextId,
      powerFraction: this.powerFraction,
      waterFraction: this.waterFraction,
      staffedFraction: this.staffedFraction,
      meteredCycles: this.meteredCycles,
      curtailedCycles: this.curtailedCycles,
      uptimeCycles: this.uptimeCycles,
      computeUnits: this.computeUnits,
      operatingCost: this.operatingCost,
      leasePaid: this.leasePaid,
    };
  }

  /** The facility types this build can site, in a fixed order. */
  static readonly TYPES = FACILITY_TYPE_NAMES;
}

/** A supplied fraction is in [0, 1]. A flow that reported otherwise has a bug. */
function clampFraction(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}
